import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_LOCALES } from '@lims/shared-types';
import { settingsApi } from '../../api/endpoints.js';
import { resolveApiPath } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  ErrorState,
  Form,
  FormSection,
  LoadingState,
  NumberField,
  PageHeader,
  Panel,
  SelectField,
  SwitchField,
  TextField,
  useToast,
  BilingualTextField,
  CheckboxGroupField,
  FileUploadField,
  SaveErrorNotice,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
} from '../../components/ui/index.js';
import {
  CONFIGURABLE_REQUIRED_CLIENT_FIELDS,
} from '@lims/shared-validation';
import { FinanceSettingsPanel } from './FinanceSettingsPanel.js';
import { NotificationSettingsPanel } from './NotificationSettingsPanel.js';
import { ClientPortalSettingsPanel } from './ClientPortalSettingsPanel.js';
import { NumberingSettings } from './NumberingSettings.js';
import { ReportSettingsPanel } from './ReportSettingsPanel.js';
import { SettingsIndex, SettingsSection } from './settings-index.js';
import {
  EMPTY_SETTINGS_FORM,
  settingsBody,
  settingsFormFrom,
  stillAsRefused,
  type SettingsFormValues,
} from './settings-form.js';
import { currentLocale } from '../../i18n/index.js';

/** The country's name in the interface language, for a valid two-letter code. */
function countryName(code: string, locale: string): string | null {
  const trimmed = code.trim();
  if (!/^[A-Za-z]{2}$/.test(trimmed)) return null;
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(trimmed.toUpperCase()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Laboratory settings.
 *
 * These are the defaults recorded during planning, all configurable per
 * laboratory rather than baked into the product: interface language, time zone,
 * currency, VAT rate, payment gating and partial reports.
 *
 * The hints beside the workflow switches state what each option does NOT do,
 * because those are the confusions the requirements specifically warn about:
 * restricting delivery is not the same as invalidating a result, and a partial
 * report does not mean the request is complete.
 *
 * Saving reports what ACTUALLY happened: the confirmation is raised from the
 * mutation's success, never from the press of the button. And a save that
 * fails NEVER DESTROYS WHAT WAS TYPED (D205): every value stays, only the
 * fields the server refused are marked, and the first of them is focused.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();

  const canEdit = can(PERMISSIONS.SETTINGS_UPDATE);

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  /**
   * The form's values (D205): loaded from the laboratory's settings, then
   * authoritative while the person edits. Neither a refetch nor a save by one
   * of the separately saved panels below replaces what has been typed here;
   * only this form's own successful save resets it. The blanks it starts from
   * are never product defaults — see `EMPTY_SETTINGS_FORM`.
   */
  const draft = useServerForm(settings.data, settingsFormFrom, EMPTY_SETTINGS_FORM);
  const form = draft.values;

  /**
   * Set or clear the laboratory's logo.
   *
   * Kept separate from the main form save because it is a different kind of
   * act: the file is already stored by the time this runs, and this only
   * points the laboratory's settings at it. A logo is a REFERENCE to a stored
   * file — no laboratory's branding is ever a literal in this codebase
   * (CLAUDE.md section 6.1).
   */
  const setLogo = useMutation({
    mutationFn: (logoFileId: string | null) =>
      settingsApi.update({ logoFileId }),
    onSuccess: async () => {
      // Raised from the SERVER's confirmation, not from the file being chosen.
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  /**
   * Saving (D205). The values are taken when Save is pressed and kept with the
   * mutation, so a refusal is matched to exactly what was refused. A failure —
   * 400, 409, 500, a timeout, no network — changes nothing on screen but the
   * error: no value is reset, cleared, refetched over or navigated away from.
   * Only a success resets the form, from the copy the server returned.
   */
  const save = useMutation({
    mutationFn: (submission: { values: SettingsFormValues; mark: number }) =>
      settingsApi.update(settingsBody(submission.values, settings.data)),
    onSuccess: async (saved, submission) => {
      // Raised from the SERVER's confirmation, not from the button press.
      draft.saved(saved, submission.mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const update = draft.set;

  /**
   * The server's refusal, field by field (`details[].field`). Each complaint
   * sits beneath its own field and lasts only while that field still holds
   * the value that was refused; the notice by the Save button is secondary.
   * A format rule gets this page's own sentence, which says what the field
   * needs rather than only that it is wrong.
   */
  const fieldMessages = {
    name: t('settings.errors.name'),
    contactEmail: t('settings.errors.contactEmail'),
    contactPhone: t('settings.errors.contactPhone'),
    website: t('settings.errors.website'),
    countryCode: t('settings.errors.countryCode'),
    currency: t('settings.errors.currency'),
    // The laboratory's own references (D206): only generic limits remain.
    vatRegistrationNumber: t('settings.errors.reference'),
    commercialRegistrationNumber: t('settings.errors.reference'),
    bankAccountNumber: t('settings.errors.reference'),
    bankIban: t('settings.errors.reference'),
    bankSwiftCode: t('settings.errors.reference'),
  };
  const fieldLabels = {
    name: t('settings.fieldNames.name'),
    'name.ar': t('settings.nameAr'),
    'name.en': t('settings.nameEn'),
    addressLine: t('settings.fieldNames.addressLine'),
    'addressLine.ar': t('settings.addressAr'),
    'addressLine.en': t('settings.addressEn'),
    city: t('settings.fieldNames.city'),
    'city.ar': t('settings.cityAr'),
    'city.en': t('settings.cityEn'),
    countryCode: t('settings.countryCode'),
    contactEmail: t('settings.contactEmail'),
    contactPhone: t('settings.contactPhone'),
    website: t('settings.website'),
    vatRegistrationNumber: t('settings.organisation.vatRegistrationNumber'),
    commercialRegistrationNumber: t('settings.organisation.commercialRegistrationNumber'),
    bankAccountName: t('settings.bank.accountName'),
    bankName: t('settings.bank.bankName'),
    bankAccountNumber: t('settings.bank.accountNumber'),
    bankIban: t('settings.bank.iban'),
    bankSwiftCode: t('settings.bank.swift'),
    defaultLocale: t('settings.defaultLocale'),
    timezone: t('settings.timezone'),
    currency: t('settings.currency'),
    taxRatePercent: t('settings.taxRatePercent'),
    quotationValidityDays: t('settings.quotationValidityDays'),
    quotationIntroduction: t('settings.fieldNames.quotationIntroduction'),
    'quotationIntroduction.ar': t('settings.quotationIntroductionAr'),
    'quotationIntroduction.en': t('settings.quotationIntroductionEn'),
    quotationPaymentTerms: t('settings.fieldNames.quotationPaymentTerms'),
    'quotationPaymentTerms.ar': t('settings.quotationPaymentTermsAr'),
    'quotationPaymentTerms.en': t('settings.quotationPaymentTermsEn'),
    quotationTerms: t('settings.fieldNames.quotationTerms'),
    'quotationTerms.ar': t('settings.quotationTermsAr'),
    'quotationTerms.en': t('settings.quotationTermsEn'),
    quotationFooterText: t('settings.fieldNames.quotationFooterText'),
    'quotationFooterText.ar': t('settings.quotationFooterTextAr'),
    'quotationFooterText.en': t('settings.quotationFooterTextEn'),
    clientRequiredFields: t('settings.clientPolicy.required'),
  };
  const { fields: serverErrors } = useSaveErrors(save.error, fieldMessages);
  const refused = save.variables?.values;
  const errorOf = (field: string): string | undefined => {
    const message = serverErrors[field];
    return message && (!refused || stillAsRefused(field, form, refused)) ? message : undefined;
  };
  const errorProps = (field: string) => {
    const message = errorOf(field);
    return message ? { error: message } : {};
  };
  const pairErrorProps = (field: string) => {
    const pair = errorOf(field);
    const arabic = errorOf(`${field}.ar`);
    const english = errorOf(`${field}.en`);
    return {
      ...(pair ? { error: pair } : {}),
      ...(arabic ? { errorAr: arabic } : {}),
      ...(english ? { errorEn: english } : {}),
    };
  };

  // The first field refused is scrolled to and focused; a failure naming no
  // field focuses the notice instead.
  const formRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(save.error, formRef, noticeRef);

  if (settings.isLoading) return <LoadingState />;
  if (settings.error) {
    return (
      <ErrorState
        error={settings.error}
        onRetry={() => void settings.refetch()}
      />
    );
  }

  const localeOptions = ALL_LOCALES.map((value) => ({
    value,
    label: value === 'ar' ? t('common.arabic') : t('common.english'),
  }));

  return (
    <>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        breadcrumbs={[{ label: t('nav.settings') }]}
      />

      <div className="grid items-start gap-gutter lg:grid-cols-[13rem_minmax(0,1fr)]">
      <SettingsIndex
        groups={[
          {
            label: t('settings.index.profile'),
            items: [
              { id: 'settings-identity', label: t('settings.identity') },
              { id: 'settings-contact', label: t('settings.contactInformation') },
              { id: 'settings-registration', label: t('settings.organisation.registrationTitle') },
              { id: 'settings-bank', label: t('settings.bank.title') },
              { id: 'settings-branding', label: t('settings.branding') },
              { id: 'settings-regional', label: t('settings.regional') },
              { id: 'settings-financial', label: t('settings.financial') },
              { id: 'settings-quotation', label: t('settings.quotationPolicy') },
              { id: 'settings-client-policy', label: t('settings.clientPolicy.title') },
              { id: 'settings-workflow', label: t('settings.workflow') },
            ],
          },
          {
            label: t('settings.index.separate'),
            items: [
              { id: 'settings-reports', label: t('settings.reports.title') },
              { id: 'settings-finance', label: t('settings.finance.title') },
              { id: 'settings-portal', label: t('settings.clientPortal.title') },
              { id: 'settings-notifications', label: t('settings.notifications.title') },
              { id: 'settings-numbering', label: t('settings.numbering.title') },
              { id: 'settings-structure', label: t('settings.structure.title') },
            ],
          },
        ]}
      />
      <div className="flex min-w-0 flex-col gap-gutter">
      <div ref={formRef}>
      <Form
        onSubmit={() => save.mutate({ values: form, mark: draft.mark() })}
        isSubmitting={save.isPending}
      >
        <div className="flex flex-col gap-gutter">
          <SettingsSection id="settings-identity">
            <Panel title={t('settings.identity')}>
              <div className="flex flex-col gap-gutter">
                {/*
                  Both languages are stored side by side, and entered data is
                  NEVER auto-translated from one into the other (CLAUDE.md
                  section 7). Each half carries its own content direction — see
                  BilingualTextField.
                */}
                <BilingualTextField
                  labelAr={t('settings.nameAr')}
                  labelEn={t('settings.nameEn')}
                  valueAr={form.nameAr}
                  valueEn={form.nameEn}
                  onChangeAr={(value) => update('nameAr', value)}
                  onChangeEn={(value) => update('nameEn', value)}
                  {...pairErrorProps('name')}
                  isReadOnly={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.addressAr')}
                  labelEn={t('settings.addressEn')}
                  valueAr={form.addressAr}
                  valueEn={form.addressEn}
                  onChangeAr={(value) => update('addressAr', value)}
                  onChangeEn={(value) => update('addressEn', value)}
                  {...pairErrorProps('addressLine')}
                  isReadOnly={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.cityAr')}
                  labelEn={t('settings.cityEn')}
                  valueAr={form.cityAr}
                  valueEn={form.cityEn}
                  onChangeAr={(value) => update('cityAr', value)}
                  onChangeEn={(value) => update('cityEn', value)}
                  {...pairErrorProps('city')}
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.countryCode')}
                  description={
                    countryName(form.countryCode, currentLocale()) ??
                    t('settings.countryCodeHint')
                  }
                  value={form.countryCode}
                  onChange={(value) => update('countryCode', value)}
                  {...errorProps('countryCode')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
              </div>
            </Panel>
          </SettingsSection>

          <SettingsSection id="settings-contact">
            <Panel title={t('settings.contactInformation')}>
              <FormSection title={t('settings.contactInformation')} columns={1}>
                <TextField
                  type="email"
                  label={t('settings.contactEmail')}
                  value={form.contactEmail}
                  onChange={(value) => update('contactEmail', value)}
                  {...errorProps('contactEmail')}
                  isReadOnly={!canEdit}
                />
                <TextField
                  type="tel"
                  label={t('settings.contactPhone')}
                  value={form.contactPhone}
                  onChange={(value) => update('contactPhone', value)}
                  {...errorProps('contactPhone')}
                  isReadOnly={!canEdit}
                />
                <TextField
                  type="url"
                  label={t('settings.website')}
                  description={t('settings.websiteHint')}
                  value={form.website}
                  onChange={(value) => update('website', value)}
                  {...errorProps('website')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
              </FormSection>
            </Panel>
          </SettingsSection>

          {/*
            The laboratory's OWN registration numbers. Settings, not literals:
            they appear on quotations, invoices and reports, and they differ
            for every laboratory that buys this product (CLAUDE.md 6.1).
          */}
          <SettingsSection id="settings-registration">
            <Panel
              title={t('settings.organisation.registrationTitle')}
              description={t('settings.organisation.registrationDescription')}
            >
              <FormSection
                title={t('settings.organisation.registrationTitle')}
                columns={1}
              >
                <TextField
                  label={t('settings.organisation.vatRegistrationNumber')}
                  value={form.vatRegistrationNumber}
                  onChange={(value) => update('vatRegistrationNumber', value)}
                  {...errorProps('vatRegistrationNumber')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.organisation.commercialRegistrationNumber')}
                  value={form.commercialRegistrationNumber}
                  onChange={(value) =>
                    update('commercialRegistrationNumber', value)
                  }
                  {...errorProps('commercialRegistrationNumber')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
              </FormSection>
            </Panel>
          </SettingsSection>

          {/*
            Printed on every quotation from now on (D199). One value in every
            language: an IBAN is the same number whoever reads it. The server
            checks the IBAN's checksum and the SWIFT code's shape.
          */}
          <SettingsSection id="settings-bank">
            <Panel
              title={t('settings.bank.title')}
              description={t('settings.bank.description')}
            >
              <FormSection title={t('settings.bank.title')} columns={1}>
                <TextField
                  label={t('settings.bank.accountName')}
                  value={form.bankAccountName}
                  onChange={(value) => update('bankAccountName', value)}
                  {...errorProps('bankAccountName')}
                  textDirection="auto"
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.bank.bankName')}
                  value={form.bankName}
                  onChange={(value) => update('bankName', value)}
                  {...errorProps('bankName')}
                  textDirection="auto"
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.bank.accountNumber')}
                  value={form.bankAccountNumber}
                  onChange={(value) => update('bankAccountNumber', value)}
                  {...errorProps('bankAccountNumber')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.bank.iban')}
                  description={t('settings.bank.ibanHint')}
                  value={form.bankIban}
                  onChange={(value) => update('bankIban', value)}
                  {...errorProps('bankIban')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.bank.swift')}
                  description={t('settings.bank.swiftHint')}
                  value={form.bankSwiftCode}
                  onChange={(value) => update('bankSwiftCode', value)}
                  {...errorProps('bankSwiftCode')}
                  textDirection="ltr"
                  isReadOnly={!canEdit}
                />
              </FormSection>
            </Panel>
          </SettingsSection>

          <SettingsSection id="settings-branding">
            <Panel title={t('settings.branding')}>
              <div className="flex flex-col gap-gutter">
                {settings.data?.logo ? (
                  <div className="flex flex-wrap items-center gap-stack">
                    {/*
                      Served by the API from this laboratory's own settings,
                      addressed by the laboratory's slug — never a storage key
                      and never a filesystem path.
                    */}
                    <img
                      src={resolveApiPath(settings.data.logo.url)}
                      alt={t('settings.logoAlt')}
                      className="max-h-16 rounded-medium border border-default-200 bg-white p-1"
                    />
                    {canEdit ? (
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        isLoading={setLogo.isPending}
                        onPress={() => setLogo.mutate(null)}
                      >
                        {t('settings.removeLogo')}
                      </ActionButton>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-caption text-default-600">
                    {t('settings.noLogo')}
                  </p>
                )}

                {canEdit ? (
                  <FileUploadField
                    entityType="tenant_logo"
                    label={t('settings.logo')}
                    description={t('settings.logoHint')}
                    accept="image/png,image/jpeg,image/webp,image/gif,image/tiff"
                    onUploaded={(file) => setLogo.mutate(file.id)}
                  />
                ) : null}

                {setLogo.error ? <SaveErrorNotice error={setLogo.error} /> : null}
              </div>
            </Panel>
          </SettingsSection>

          <SettingsSection id="settings-regional">
            <Panel title={t('settings.regional')}>
              <div className="flex flex-col gap-gutter">
                <SelectField
                  label={t('settings.defaultLocale')}
                  options={localeOptions}
                  value={form.defaultLocale}
                  onChange={(value) =>
                    update('defaultLocale', value ?? form.defaultLocale)
                  }
                  {...errorProps('defaultLocale')}
                  isDisabled={!canEdit}
                />
                <TextField
                  label={t('settings.timezone')}
                  description={t('settings.timezoneHint')}
                  value={form.timezone}
                  onChange={(value) => update('timezone', value)}
                  {...errorProps('timezone')}
                  isReadOnly={!canEdit}
                />
                <TextField
                  label={t('settings.currency')}
                  value={form.currency}
                  onChange={(value) =>
                    update('currency', value.toUpperCase().slice(0, 3))
                  }
                  {...errorProps('currency')}
                  isReadOnly={!canEdit}
                />
                <SwitchField
                  label={t('settings.showHijriDates')}
                  value={form.showHijriDates}
                  onChange={(value) => update('showHijriDates', value)}
                  isDisabled={!canEdit}
                />
              </div>
            </Panel>
          </SettingsSection>

          <SettingsSection id="settings-financial">
            <Panel title={t('settings.financial')}>
              <NumberField
                label={t('settings.taxRatePercent')}
                description={t('settings.taxRateHint')}
                value={form.taxRatePercent}
                onChange={(value) => update('taxRatePercent', value)}
                {...errorProps('taxRatePercent')}
                step="0.01"
                isDisabled={!canEdit}
              />
            </Panel>
          </SettingsSection>

          {/*
            The laboratory's own quotation policy. A default validity period
            and the standard terms printed at the foot of every quotation —
            both SETTINGS, neither a literal anywhere in this codebase, and
            neither restated by this page (decision D42).

            Editing the terms here never changes a quotation already
            written: each one carries the terms it was written with.
          */}
          <SettingsSection id="settings-quotation">
            <Panel title={t('settings.quotationPolicy')}>
              <FormSection title={t('settings.quotationPolicy')} columns={1}>
                <NumberField
                  label={t('settings.quotationValidityDays')}
                  description={t('settings.quotationValidityHint')}
                  value={form.quotationValidityDays}
                  onChange={(value) => update('quotationValidityDays', value)}
                  {...errorProps('quotationValidityDays')}
                  isDisabled={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.quotationIntroductionAr')}
                  labelEn={t('settings.quotationIntroductionEn')}
                  valueAr={form.quotationIntroductionAr}
                  valueEn={form.quotationIntroductionEn}
                  onChangeAr={(value) => update('quotationIntroductionAr', value)}
                  onChangeEn={(value) => update('quotationIntroductionEn', value)}
                  {...pairErrorProps('quotationIntroduction')}
                  descriptionAr={t('settings.quotationTermsHint')}
                  descriptionEn={t('settings.quotationTermsHint')}
                  rows={3}
                  isDisabled={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.quotationPaymentTermsAr')}
                  labelEn={t('settings.quotationPaymentTermsEn')}
                  valueAr={form.quotationPaymentTermsAr}
                  valueEn={form.quotationPaymentTermsEn}
                  onChangeAr={(value) => update('quotationPaymentTermsAr', value)}
                  onChangeEn={(value) => update('quotationPaymentTermsEn', value)}
                  {...pairErrorProps('quotationPaymentTerms')}
                  rows={2}
                  isDisabled={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.quotationTermsAr')}
                  labelEn={t('settings.quotationTermsEn')}
                  valueAr={form.quotationTermsAr}
                  valueEn={form.quotationTermsEn}
                  onChangeAr={(value) => update('quotationTermsAr', value)}
                  onChangeEn={(value) => update('quotationTermsEn', value)}
                  {...pairErrorProps('quotationTerms')}
                  rows={5}
                  isDisabled={!canEdit}
                />
                <BilingualTextField
                  labelAr={t('settings.quotationFooterTextAr')}
                  labelEn={t('settings.quotationFooterTextEn')}
                  valueAr={form.quotationFooterTextAr}
                  valueEn={form.quotationFooterTextEn}
                  onChangeAr={(value) => update('quotationFooterTextAr', value)}
                  onChangeEn={(value) => update('quotationFooterTextEn', value)}
                  {...pairErrorProps('quotationFooterText')}
                  descriptionAr={t('settings.quotationFooterHint')}
                  descriptionEn={t('settings.quotationFooterHint')}
                  isDisabled={!canEdit}
                />
              </FormSection>
            </Panel>
          </SettingsSection>


          {/*
            This laboratory's own intake policy. The PRODUCT requires only a
            name, so a reception desk can always record the person standing
            in front of it; a laboratory with stricter rules opts in here,
            and can opt back out the day the rule gets in the way
            (decision D35).
          */}
          <SettingsSection id="settings-client-policy">
            <Panel
              title={t('settings.clientPolicy.title')}
              description={t('settings.clientPolicy.description')}
            >
              <FormSection title={t('settings.clientPolicy.title')} columns={1}>
                <CheckboxGroupField
                  label={t('settings.clientPolicy.required')}
                  value={form.clientRequiredFields}
                  onChange={(value) => update('clientRequiredFields', value)}
                  {...errorProps('clientRequiredFields')}
                  isDisabled={!canEdit}
                  options={CONFIGURABLE_REQUIRED_CLIENT_FIELDS.map((field) => ({
                    value: field,
                    label: t(`settings.clientPolicy.field.${field}`),
                  }))}
                />
              </FormSection>
            </Panel>
          </SettingsSection>

          <SettingsSection id="settings-workflow">
            <Panel title={t('settings.workflow')}>
              <FormSection title={t('settings.workflow')} columns={1}>
                <SwitchField
                  label={t('settings.requirePaymentBeforeDelivery')}
                  description={t('settings.requirePaymentHint')}
                  value={form.requirePaymentBeforeDelivery}
                  onChange={(value) =>
                    update('requirePaymentBeforeDelivery', value)
                  }
                  isDisabled={!canEdit}
                />
                <SwitchField
                  label={t('settings.allowPartialReports')}
                  description={t('settings.allowPartialHint')}
                  value={form.allowPartialReports}
                  onChange={(value) => update('allowPartialReports', value)}
                  isDisabled={!canEdit}
                />
              </FormSection>
            </Panel>
          </SettingsSection>

          {save.error ? (
            <div ref={noticeRef} tabIndex={-1} className="outline-none">
              <SaveErrorNotice
                error={save.error}
                title={t('settings.saveFailed')}
                fieldLabels={fieldLabels}
                fieldMessages={fieldMessages}
              />
            </div>
          ) : null}

          {canEdit ? (
            <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-stack rounded-large border border-default-200 bg-white px-4 py-3">
              <p className="min-w-0 flex-1 text-caption text-default-600">
                {t('settings.index.profileSaveHint')}
              </p>
              <ActionBar>
                <ActionButton
                  priority="primary"
                  type="submit"
                  isLoading={save.isPending}
                >
                  {t('settings.save')}
                </ActionButton>
              </ActionBar>
            </div>
          ) : null}
        </div>
      </Form>
      </div>

      {/*
        Report settings (Phase 10, D111/D126): the footer wording and whether a
        verification link-holder may download the original. Saved on their own,
        outside the form above, so the public-download switch never rides along
        with an unrelated edit to the tax rate.
      */}
      {settings.data ? (
        <SettingsSection id="settings-reports">
          <ReportSettingsPanel settings={settings.data} />
        </SettingsSection>
      ) : null}

      {/*
        Finance settings (Phase 11): due-date terms for a draft that names no
        due date of its own. Saved on its own, outside the form above, for the
        same reason the report settings are.
      */}
      {settings.data ? (
        <>
          <SettingsSection id="settings-finance">
            <FinanceSettingsPanel settings={settings.data} />
          </SettingsSection>
          <SettingsSection id="settings-portal">
            <ClientPortalSettingsPanel settings={settings.data} />
          </SettingsSection>
          <SettingsSection id="settings-notifications">
            <NotificationSettingsPanel settings={settings.data} />
          </SettingsSection>
        </>
      ) : null}

      {/*
        Outside the settings form on purpose. Each numbering rule is saved on
        its own endpoint, one record type at a time, so a change to the client
        format cannot ride along with an unrelated edit to the tax rate.
      */}
      <SettingsSection id="settings-numbering">
        <NumberingSettings />
      </SettingsSection>

      {/*
        The laboratory's own structure, each on its own page.

        Branches and departments are LISTS THE LABORATORY OWNS, not settings
        with one value, and neither is seeded with anything — not even a "Main
        Branch". A pilot laboratory's own list of branches is exactly the
        organisation-specific value that must never be a literal in this
        codebase (CLAUDE.md section 6.1, decision D53).
      */}
      <SettingsSection id="settings-structure">
        <Panel
          title={t('settings.structure.title')}
          description={t('settings.structure.description')}
        >
          <div className="flex flex-wrap gap-inline">
            <ActionButton
              priority="secondary"
              onPress={() => navigate('/settings/branches')}
            >
              {t('settings.structure.branches')}
            </ActionButton>
            <ActionButton
              priority="secondary"
              onPress={() => navigate('/settings/departments')}
            >
              {t('settings.structure.departments')}
            </ActionButton>
            {/*
              The sample type builder (0024): the laboratory's own types and
              their additional intake fields, with each type's receiving rules
              and retention period (D70, D71).
            */}
            <ActionButton
              priority="secondary"
              onPress={() => navigate('/settings/sample-types')}
            >
              {t('settings.structure.sampleTypes')}
            </ActionButton>
            {/*
              The specification register (Phase 8, D87). It starts empty: the
              standards a laboratory works to are its own facts.
            */}
            <ActionButton
              priority="secondary"
              onPress={() => navigate('/settings/specifications')}
            >
              {t('settings.structure.specifications')}
            </ActionButton>
          </div>
        </Panel>
      </SettingsSection>
      </div>
      </div>
    </>
  );
}
